import { IUploadFile } from "@app-types/websocket/file.types";
import { cachePut } from "@services/file/upload.cache";

let internalFileId:number = Date.now();
const nextId = () => {
  const id = internalFileId + 1;
  internalFileId = id;
  return id;
};

const handleFileUpload = (file: File, uploadFile: (file: IUploadFile) => void): { internalId: number } => {
  const id = nextId();
  const mime = mimeFromFilename(file.name);
  const body = file.slice(0, file.size, mime);
  cachePut(id, { body, name: file.name, size: body.size });
  uploadFile({ name: file.name, size: body.size, internalId: id });
  return { internalId: id };
};

const handleBlobUpload = (blob: Blob, fileName: string, uploadFile: (file: IUploadFile) => void): { internalId: number } => {
  const id = nextId();
  const mime = mimeFromFilename(fileName);

  cachePut(id, { body:blob, name: fileName, size: blob.size });
  uploadFile({ name: fileName, size: blob.size, internalId: id });

  return { internalId: id };
};

const handleImageUpload = (
  blob: Blob,
  fileName: string,
  dataUrl: string,
  uploadFile: (file: IUploadFile) => void
): { internalId: number } => {
  const id = nextId();

  cachePut(id, { body:blob, name: fileName, size: blob.size, localURL: dataUrl });
  uploadFile({ name: fileName, size: blob.size, internalId: id, localURL: dataUrl });

  return { internalId: id };
};

const getCanvasBlobFromImageFile = (file: File, maxWidth:number, maxHeight:number): Promise<Blob | null> => {
  return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
          const dataURL = reader.result as string;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();

          img.onload = () => {
              if (!ctx) {
                  reject(new Error('Could not get canvas context'));
                  return;
              }


              let newImgWidth = img.width;
              let newImgHeight = img.height;
            
              if (img.width >= img.height && img.width > maxWidth) {
                newImgWidth = maxWidth;
                newImgHeight = Math.floor(((maxWidth*img.height)/img.width));
              } else if (img.height > img.width && img.height > maxHeight) {
                newImgHeight = maxHeight;
                newImgWidth = Math.floor(((maxHeight*img.width)/img.height));
              }
    
    
              canvas.width = newImgWidth;
              canvas.height = newImgHeight;
              
              ctx.imageSmoothingQuality = "high";
              ctx.drawImage(img, 0, 0, newImgWidth, newImgHeight);

              canvas.toBlob((blob) => {
                  resolve(blob);
              });
          };

          img.onerror = (error) => {
              reject(error);
          };

          img.src = dataURL;
      };

      reader.onerror = (error) => {
          reject(error);
      };

      reader.readAsDataURL(file);
  });
};


const getCanvasDataURLFromFile = (file: File, maxWidth:number, maxHeight:number): Promise<string | null> => {
  return new Promise((resolve, reject) => {
      // Check if the file is an image
      if (!file.type.startsWith('image/')) {
          reject(new Error('The selected file is not an image.'));
          return;
      }

      const reader = new FileReader();

      reader.onload = () => {
          const dataURL = reader.result as string;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = new Image();

          img.onload = () => {
              if (!ctx) {
                  reject(new Error('Could not get canvas context'));
                  return;
              }

              let newImgWidth = img.width;
              let newImgHeight = img.height;
            
              if (img.width >= img.height && img.width > maxWidth) {
                newImgWidth = maxWidth;
                newImgHeight = Math.floor(((maxWidth*img.height)/img.width));
              } else if (img.height > img.width && img.height > maxHeight) {
                newImgHeight = maxHeight;
                newImgWidth = Math.floor(((maxHeight*img.width)/img.height));
              }

              canvas.width = newImgWidth;
              canvas.height = newImgHeight;
              
              ctx.imageSmoothingQuality = "high";
              ctx.drawImage(img, 0, 0, newImgWidth, newImgHeight);

              const canvasDataURL = canvas.toDataURL();
              resolve(canvasDataURL);
          };

          img.onerror = (error) => {
              reject(error);
          };

          img.src = dataURL;
      };

      reader.onerror = (error) => {
          reject(error);
      };

      reader.readAsDataURL(file);
  });
};

const fileToBase64 = (file: File) => {
    return new Promise((res) => {
        const reader = new FileReader();
        reader.onload = (event: any) => {
            const base64String = event.target.result.split(',')[1];
            res(base64String);
        };
        reader.readAsDataURL(file);
    })
}

const base64ToBlob = (base64: string, mimeType: string): Blob => {
  const byteString = atob(base64.split(',')[1]);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeType });
}

const dataURLToBlob = (dataURL: string): Blob => {
  const byteString = atob(dataURL.split(',')[1]);
  const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
  
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ab], { type: mimeString });
};

export const getCanvasBlobFromDataURL = (dataURL: string, maxWidth: number, maxHeight: number): Promise<Blob | null> => {
  return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
          if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
          }

          let newImgWidth = img.width;
          let newImgHeight = img.height;

          if (img.width >= img.height && img.width > maxWidth) {
              newImgWidth = maxWidth;
              newImgHeight = Math.floor((maxWidth * img.height) / img.width);
          } else if (img.height > img.width && img.height > maxHeight) {
              newImgHeight = maxHeight;
              newImgWidth = Math.floor((maxHeight * img.width) / img.height);
          }

          canvas.width = newImgWidth;
          canvas.height = newImgHeight;

          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, 0, 0, newImgWidth, newImgHeight);

          canvas.toBlob((blob) => {
              resolve(blob);
          });
      };

      img.onerror = (error) => {
          reject(error);
      };

      img.src = dataURL;
  });
};


// --- helpers ---
const mimeFromFilename =(name?: string): string  => {
  if (!name) return "application/octet-stream";
  const ext = name.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'png': return 'image/png';
    case 'gif': return 'image/gif';
    case 'webp': return 'image/webp';
    case 'bmp': return 'image/bmp';
    case 'svg': return 'image/svg+xml';
    case 'pdf': return 'application/pdf';
    case 'txt': return 'text/plain';
    case 'csv': return 'text/csv';
    case 'md': return 'text/markdown';
    case 'rtf': return 'application/rtf';
    case 'json': return 'application/json';
    case 'zip': return 'application/zip';
    case 'doc': return 'application/msword';
    case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls': return 'application/vnd.ms-excel';
    case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'ppt': return 'application/vnd.ms-powerpoint';
    case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    case 'mp4': return 'video/mp4';
    case 'mov': return 'video/quicktime';
    default: return "application/octet-stream";
  }
}

const isImageFile = (name?: string): boolean => {
  return mimeFromFilename(name).startsWith("image/");
}

const openFileInNewTab = (url?: string): void => {
  if (!url) {return;}
  const w:any = window;
  if (w.cordova?.InAppBrowser) {
    w.cordova.InAppBrowser.open(url, "_system", "location=yes");
  } else {
    window.open(url, "_blank");
  }
}

export { getCanvasDataURLFromFile, getCanvasBlobFromImageFile, handleImageUpload, handleFileUpload,
    handleBlobUpload, base64ToBlob, dataURLToBlob, fileToBase64, mimeFromFilename, isImageFile, openFileInNewTab };



// WEBPACK FOOTER //
// ./src/helpers/uploadHelper.ts